import type { Catalogue, Tier as TierName } from "../data/schema";

/** Tier as a number in typed arrays; read it through these names, never as literals. */
export const TIER_CODE = { textured: 0, simple: 1, point: 2 } as const;

/** Per-body facts derived once from the catalogue, indexed like the catalogue. */
export class BodyTable {
  readonly count: number;
  readonly index = new Map<string, number>();
  /** Parent's index, -1 for the root. */
  readonly parentIndex: Int32Array;
  /** Index of the body without a parent (the Sun). */
  readonly root: number;
  /** TIER_CODE per body. */
  readonly tier: Uint8Array;
  readonly byTier: Record<TierName, number[]> = { textured: [], simple: [], point: [] };
  readonly radiusKm: Float32Array;
  /** Semi-major axis in km, 0 for a body without an orbit. */
  readonly orbitKm: Float64Array;
  /** Parent's index for a moon, -1 for every other body. */
  readonly moonParent: Int32Array;
  /** Moons per body. */
  readonly moonCount: Uint16Array;
  /** Planets and dwarf planets, in catalogue order. */
  readonly planets: readonly number[];

  private constructor(readonly bodies: Catalogue) {
    this.count = bodies.length;
    bodies.forEach((b, k) => this.index.set(b.id, k));
    // parseCatalogue guarantees every parent exists and precedes its children
    this.parentIndex = Int32Array.from(bodies, (b) =>
      b.parent === null ? -1 : this.index.get(b.parent)!,
    );
    this.root = this.parentIndex.indexOf(-1);
    this.tier = Uint8Array.from(bodies, (b) => TIER_CODE[b.tier]);
    bodies.forEach((b, k) => this.byTier[b.tier].push(k));
    this.radiusKm = Float32Array.from(bodies, (b) => b.physical.radiusKm);
    this.orbitKm = Float64Array.from(bodies, (b) => b.orbit?.a ?? 0);
    this.moonParent = Int32Array.from(bodies, (b, k) =>
      b.kind === "moon" ? this.parentIndex[k] : -1,
    );
    this.moonCount = new Uint16Array(this.count);
    for (const parent of this.moonParent) if (parent >= 0) this.moonCount[parent]++;
    this.planets = bodies.flatMap((b, k) => (b.kind === "planet" || b.kind === "dwarf" ? [k] : []));
  }

  static from(bodies: Catalogue): BodyTable {
    return new BodyTable(bodies);
  }

  /** -1 for an unknown id. */
  indexOf(id: string): number {
    return this.index.get(id) ?? -1;
  }
}
